import { IProperty } from '../IProperty.js';
import { IDesignItem } from '../../../item/IDesignItem.js';
import { PropertyType } from '../PropertyType.js';
import { RefreshMode } from '../IPropertiesService.js';
import { IPropertyGroup } from '../IPropertyGroup.js';
import { IStyleDeclaration, IStyleRule } from '../../stylesheetService/IStylesheetService.js';
import { ValueType } from '../ValueType.js';
import { NodeType } from '../../../item/NodeType.js';
import { BindingTarget } from '../../../item/BindingTarget.js';
import { PropertiesHelper } from './PropertiesHelper.js';
import { AbstractPropertiesService } from './AbstractPropertiesService.js';
import { CssPropertiesService } from './CssPropertiesService.js';
import { appendCssImportant, splitCssImportant } from '../../../helper/CssImportant.js';

const localName = '&lt;local&gt;';

export class CssCurrentPropertiesService extends CssPropertiesService {

  public override getRefreshMode(designItem: IDesignItem) {
    return RefreshMode.fullOnValueChange;
  }

  constructor() {
    super(null);
    this.name = 'styles';
  }

  override isHandledElement(designItem: IDesignItem): boolean {
    return true;
  }

  override async getProperty(designItem: IDesignItem, name: string): Promise<IProperty> {
    const camelName = PropertiesHelper.dashToCamelCase(name);
    return this._enrichCssProperty({ name: name, type: this._getPropertyType(camelName), values: this._getPropertyValues(camelName), service: this, propertyType: PropertyType.cssValue });
  }

  override async getProperties(designItem: IDesignItem): Promise<IProperty[] | IPropertyGroup[]> {
    if (!designItem || designItem.nodeType != NodeType.Element)
      return [];

    let styles = designItem.getAllStyles().reverse().sort((a, b) => {
      if (a.specificity == null)
        return -1;
      if (b.specificity == null)
        return 1;
      if (a.specificity.A > b.specificity.A)
        return -1;
      if (a.specificity.A === b.specificity.A && a.specificity.B > b.specificity.B)
        return -1;
      if (a.specificity.A === b.specificity.A && a.specificity.B === b.specificity.B && a.specificity.C > b.specificity.C)
        return -1;
      if (a.specificity.A === b.specificity.A && a.specificity.B === b.specificity.B && a.specificity.C === b.specificity.C)
        return 0;
      return 1;
    });

    const bindings = AbstractPropertiesService.getOrBuildCachedBindings(designItem);
    let localStyles = styles.find(x => x.selector === null)
    for (const b of bindings) {
      if (b.target === BindingTarget.css) {
        if (!localStyles.declarations.find(x => x.name == b.targetName)) {
          localStyles.declarations.push({ name: b.targetName, value: null, important: false, parent: null });
        }
      }
    }

    let arr = styles.map(x => ({
      name: (x.selector ?? localName) + (x.specificity ? ' (' + x.specificity.A + '-' + x.specificity.B + '-' + x.specificity.C + ')' : ''), description: x.stylesheetName ?? '', properties: [
        ...x.declarations.map(y => {
          const camelName = PropertiesHelper.dashToCamelCase(y.name);
          return this._enrichCssProperty({
            name: y.name,
            renamable: true,
            type: this._getPropertyType(camelName),
            values: this._getPropertyValues(camelName),
            service: this,
            propertyType: PropertyType.cssValue,
            //@ts-ignore
            styleRule: x,
            styleDeclaration: y
          })
        }),
        { name: '', type: 'addNew', service: this, propertyType: PropertyType.complex, styleRule: x }
      ]
    }));
    return arr;
  }

  override async setValue(designItems: IDesignItem[], property: (IProperty & { styleRule: IStyleRule, styleDeclaration: IStyleDeclaration }), value: any) {
    const parsedValue = typeof value === 'string' ? splitCssImportant(value) : { value, important: false };
    // No selector means local style, styleDeclaration is null means new property
    if (property.styleRule?.selector !== null && property.styleDeclaration) {
      designItems[0].instanceServiceContainer.stylesheetService.updateDeclarationValue(property.styleDeclaration, parsedValue.value, parsedValue.important);
      this._notifyChangedProperty(designItems[0], property, value);
      return;
    }
    if (property.styleRule?.selector !== null && !property.styleDeclaration) {
      designItems[0].instanceServiceContainer.stylesheetService.insertDeclarationIntoRule(property.styleRule, property.name, parsedValue.value, parsedValue.important);
      this._notifyChangedProperty(designItems[0], property, value);
      return;
    }

    for (const d of designItems) {
      // Local style
      d.setStyle(property.name, parsedValue.value, parsedValue.important);
      //unkown css property names do not trigger the mutation observer of property grid, 
      //fixed by assinging stle again to the attribute
      (<HTMLElement>d.element).setAttribute('style', (<HTMLElement>d.element).getAttribute('style'));
    }
  }

  override clearValue(designItems: IDesignItem[], property: IProperty & { styleRule: IStyleRule, styleDeclaration: IStyleDeclaration }, clearType: 'all' | 'binding' | 'value') {
    if (property.styleRule?.selector !== null && property.styleDeclaration) {
      designItems[0].instanceServiceContainer.stylesheetService.removeDeclarationFromRule(property.styleRule, property.styleDeclaration.name);
      return;
    }
    super.clearValue(designItems, property, clearType);
  }

  override getValue(designItems: IDesignItem[], property: IProperty & { styleRule: IStyleRule, styleDeclaration: IStyleDeclaration }) {
    if (property.styleRule?.selector && property.styleDeclaration)
      return appendCssImportant(property.styleDeclaration.value, property.styleDeclaration.important);
    return super.getValue(designItems, property);
  }

  override getUnsetValue(designItems: IDesignItem[], property: IProperty & { styleRule: IStyleRule, styleDeclaration: IStyleDeclaration }) {
    if (property.styleRule?.selector && property.styleDeclaration)
      return appendCssImportant(property.styleDeclaration.value, property.styleDeclaration.important);
    return super.getUnsetValue(designItems, property);
  }

  override isSet(designItems: IDesignItem[], property: IProperty & { styleRule: IStyleRule, styleDeclaration: IStyleDeclaration }): ValueType {
    if (property.styleRule?.selector && property.styleDeclaration) {
      if (designItems[0].hasStyle(property.name))
        return ValueType.none;
      //TODO: we need to check if this is the dec. with the highest specifity
      return ValueType.fromStylesheet;
    }
    return super.isSet(designItems, property);
  }

  override getPropertyTarget(designItem: IDesignItem, property: IProperty): BindingTarget {
    if (property.name.startsWith('--'))
      return BindingTarget.cssvar;
    return BindingTarget.css;
  }
}
